import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import type { AppState, Notificaciones, Perfil, Preferencias } from '../types';
import { Field, PageShell, Toggle, glassLight, inputStyle, T, primaryButton } from '../ui';
function PanelLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <div style={{
    fontSize: 11,
    fontWeight: 700,
    color: T.textMuted,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }} data-id="element-282" data-mp="3hhxwirz12sog">
      
      <span style={{
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: T.accent,
      boxShadow: `0 0 8px ${T.accent}`
    }} data-id="element-283" data-mp="2prthl8u1exum" />
      
      {children}
    </div>;
}
const panelVariant = {
  hidden: {
    opacity: 0,
    y: 14
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.34,
      ease: [0.2, 0.8, 0.2, 1]
    }
  }
};
type Tab = 'general' | 'usuarios' | 'roles';
export function Configuracion({
  state,
  setPerfil,
  setNotif,
  setPrefs,
  usuariosNode,
  rolesNode
}: {
  state: AppState;
  setPerfil: (value: Perfil) => void;
  setNotif: (value: Notificaciones) => void;
  setPrefs: (value: Preferencias) => void;
  usuariosNode?: React.ReactNode;
  rolesNode?: React.ReactNode;
}) {
  // ── Draft state local (no guarda hasta presionar "Guardar") ──────────────
  const [draftPerfil, setDraftPerfil] = useState<Perfil>({
    ...state.perfil
  });
  const [draftNotif, setDraftNotif] = useState<Notificaciones>({
    ...state.notif
  });
  const [draftPrefs, setDraftPrefs] = useState<Preferencias>({
    ...state.prefs
  });
  // Cuando cambia el usuario logueado (session), sincronizamos el draft
  useEffect(() => {
    setDraftPerfil({
      ...state.perfil
    });
    setDraftNotif({
      ...state.notif
    });
    setDraftPrefs({
      ...state.prefs
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.perfil.correo]);
  // ── Feedback de guardado ─────────────────────────────────────────────────
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // Detectar si hay cambios pendientes
  const hasChanges = JSON.stringify(draftPerfil) !== JSON.stringify(state.perfil) || JSON.stringify(draftNotif) !== JSON.stringify(state.notif) || JSON.stringify(draftPrefs) !== JSON.stringify(state.prefs);
  const handleSave = () => {
    setSaving(true);
    setPerfil(draftPerfil);
    setNotif(draftNotif);
    setPrefs(draftPrefs);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }, 350);
  };
  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabs: {
    id: Tab;
    label: string;
    show: boolean;
  }[] = [{
    id: 'general',
    label: 'General',
    show: true
  }, {
    id: 'usuarios',
    label: 'Usuarios',
    show: !!usuariosNode
  }, {
    id: 'roles',
    label: 'Roles y permisos',
    show: !!rolesNode
  }];
  const visibles = tabs.filter(t => t.show);
  const [tab, setTab] = useState<Tab>('general');
  return <PageShell title="Configuración" sub="Perfil, notificaciones, usuarios, roles y conexión del sistema" data-id="element-284" data-mp="1xt468jrffhnw">
      
      {/* ── Sub-navegación ─────────────────────────────────────────────── */}
      {visibles.length > 1 && <div style={{
      display: 'flex',
      gap: 6,
      padding: 5,
      borderRadius: 14,
      background: T.surfaceSubtle,
      border: `1px solid ${T.border}`,
      width: 'fit-content',
      marginBottom: 20,
      flexWrap: 'wrap'
    }} data-id="element-285" data-mp="1nvg4h9vel9w8">
        
          {visibles.map(t => {
        const active = tab === t.id;
        return <button key={t.id} onClick={() => setTab(t.id)} style={{
          position: 'relative',
          padding: '9px 18px',
          borderRadius: 10,
          border: 'none',
          background: active ? T.accentSoft : 'transparent',
          color: active ? T.accent : T.textMuted,
          fontSize: 13,
          fontWeight: active ? 700 : 600,
          cursor: 'pointer'
        }} data-id="element-286" data-mp="26rfx8mw7ghgv">
              
                {t.label}
              </button>;
      })}
        </div>}

      {/* ── Tab General ──────────────────────────────────────────────────── */}
      {tab === 'general' && <>
          <motion.div initial="hidden" animate="show" variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: 0.06
          }
        }
      }} style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(290px,1fr))',
        gap: 16
      }} data-id="element-287" data-mp="xl7fsf1x4vxf">
          
            {/* ── Perfil ───────────────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-288" data-mp="1gi8n46iy384j">
            
              <PanelLabel data-id="element-289" data-mp="2d3ap40i1vcu9">Perfil</PanelLabel>

              <Field label="Nombre" data-id="element-290" data-mp="289y0vrha9821">
                <input className="getr-input" style={inputStyle} value={draftPerfil.nombre} onChange={e => setDraftPerfil({
              ...draftPerfil,
              nombre: e.target.value
            })} data-id="element-291" data-mp="288wrrsjtuvmy" />
              
              </Field>
              <Field label="Correo" data-id="element-292" data-mp="3srt2lwzwrth">
                <input className="getr-input" style={inputStyle} value={draftPerfil.correo} onChange={e => setDraftPerfil({
              ...draftPerfil,
              correo: e.target.value
            })} data-id="element-293" data-mp="3rqg2bwf5v94" />
              
              </Field>
              <Field label="Rol" data-id="element-294" data-mp="dov2mcu6fsqg">
                <input className="getr-input" style={{
              ...inputStyle,
              color: T.textMuted
            }} value={draftPerfil.rol} readOnly data-id="element-295" data-mp="iozchk0kxp7p" />
              
              </Field>
            </motion.div>

            {/* ── Notificaciones ───────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-296" data-mp="wi8hz9lepqky">
            
              <PanelLabel data-id="element-297" data-mp="11gjs7lfm633p">Notificaciones</PanelLabel>
              <Toggle label="Alertas fuera de rango" checked={draftNotif.fueraRango} onChange={() => setDraftNotif({
            ...draftNotif,
            fueraRango: !draftNotif.fueraRango
          })} data-id="element-298" data-mp="2xivjiiy9ss4k" />
            
              <Toggle label="Resumen diario" checked={draftNotif.resumen} onChange={() => setDraftNotif({
            ...draftNotif,
            resumen: !draftNotif.resumen
          })} data-id="element-299" data-mp="37a9m4zliq81x" />
            
              <Toggle label="Cambios de auditoría" checked={draftNotif.auditoria} onChange={() => setDraftNotif({
            ...draftNotif,
            auditoria: !draftNotif.auditoria
          })} data-id="element-300" data-mp="3g8lbkyanezss" />
            
              <Toggle label="Enviar por correo" checked={draftNotif.email} onChange={() => setDraftNotif({
            ...draftNotif,
            email: !draftNotif.email
          })} data-id="element-301" data-mp="3q7kee08qg17v" />
            
            </motion.div>

            {/* ── Preferencias ─────────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-302" data-mp="1qwsx4ldwo6j7">
            
              <PanelLabel data-id="element-303" data-mp="bc0q0s1q5btq">Preferencias</PanelLabel>
              <Field label="Lux mínimo global" data-id="element-304" data-mp="bapeu75y91sl">
                <input className="getr-input" style={inputStyle} type="number" value={draftPrefs.luxMin} onChange={e => setDraftPrefs({
              ...draftPrefs,
              luxMin: Number(e.target.value)
            })} data-id="element-305" data-mp="3ssiat9vnd9ah" />
              
              </Field>
              <Field label="Idioma" data-id="element-306" data-mp="3jqis8d4zhx5f">
                <select className="getr-input" style={inputStyle} value={draftPrefs.idioma} onChange={e => setDraftPrefs({
              ...draftPrefs,
              idioma: e.target.value
            })} data-id="element-307" data-mp="3oqnbuc16v7zv">
                
                  <option data-id="element-308" data-mp="3erxtyj9alnjm">Español</option>
                  <option data-id="element-309" data-mp="3eszd7b0gpgcf">English</option>
                </select>
              </Field>
              <Field label="Tema" data-id="element-310" data-mp="182sx506ojtjd">
                <select className="getr-input" style={inputStyle} value={draftPrefs.tema} onChange={e => setDraftPrefs({
              ...draftPrefs,
              tema: e.target.value
            })} data-id="element-311" data-mp="1d1dxczlxmd7t">
                
                  <option data-id="element-312" data-mp="3rxse6ggfptbj">Oscuro</option>
                  <option data-id="element-313" data-mp="3ryu05fyzo081">Claro</option>
                  <option data-id="element-314" data-mp="3n7lnmb2d3yqn">Automático</option>
                </select>
              </Field>
            </motion.div>
          </motion.div>

          {/* ── Botón Guardar ─────────────────────────────────────────── */}
          <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} style={{
        marginTop: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 14
      }} data-id="element-315" data-mp="3d9xjwvms3gwc">
          
            <button onClick={handleSave} disabled={saving || !hasChanges} style={{
          ...primaryButton,
          opacity: !hasChanges || saving ? 0.5 : 1,
          cursor: !hasChanges || saving ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          transition: 'opacity 0.2s'
        }} data-id="element-316" data-mp="2za6a773aomo5">
            
              {saving ? <>
                  <span style={{
              width: 14,
              height: 14,
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: '#ffffff',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.7s linear infinite'
            }} data-id="element-317" data-mp="1a0do4kw7ulyu" />
              
                  Guardando…
                </> : <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-318" data-mp="r1hw9g7tyjo6">
                
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" data-id="element-319" data-mp="w0b8747gy2m8" />
                    <polyline points="17 21 17 13 7 13 7 21" data-id="element-320" data-mp="110yzzs3iunhi" />
                    <polyline points="7 3 7 8 15 8" data-id="element-321" data-mp="27gsx8j8ql2yu" />
                  </svg>
                  Guardar cambios
                </>}
            </button>

            {/* Indicador de cambios pendientes */}
            {hasChanges && !saved && <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} style={{
          fontSize: 12,
          color: T.textMuted
        }} data-id="element-322" data-mp="2qq0qqem2gxpp">
            
                Cambios sin guardar
              </motion.span>}

            {/* Confirmación de guardado */}
            {saved && <motion.span initial={{
          opacity: 0,
          x: -6
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0
        }} style={{
          fontSize: 13,
          fontWeight: 600,
          color: T.ok,
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-323" data-mp="34hysg4dg7rbu">
            
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-324" data-mp="2ls8uj8ee2pfp">
              
                  <polyline points="20 6 9 17 4 12" data-id="element-325" data-mp="3s7t2ke6dv1se" />
                </svg>
                ¡Guardado correctamente!
              </motion.span>}
          </motion.div>

          {/* Keyframe para el spinner */}
          <style data-id="element-326" data-mp="1llvd11bhtwan">{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>}

      {/* ── Tab Usuarios ─────────────────────────────────────────────────── */}
      {tab === 'usuarios' && usuariosNode && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-327" data-mp="1brmo0nlo8lh8">
        
          {usuariosNode}
        </motion.div>}

      {/* ── Tab Roles ────────────────────────────────────────────────────── */}
      {tab === 'roles' && rolesNode && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-328" data-mp="sulhuzgdsfbp">
        
          {rolesNode}
        </motion.div>}
    </PageShell>;
}